import React, { useEffect, useMemo, useRef, useState } from 'react';
import Split from 'react-split';
import './PeriodicInvoicing.css';
import { FaCalendarAlt, FaSearch, FaSort, FaSortUp, FaSortDown, FaArrowLeft, FaSync, FaTimes } from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;
const SORT_KEY = 'periodicInvoiceSort';
const DETAIL_SPLIT_KEY = 'pi-detail-split-v1';

// per-section sort keys
const RSORT_KEY = 'piSort.receipts';
const OSORT_KEY = 'piSort.orders';
const ESORT_KEY = 'piSort.eorders';

// simple local storage helpers
function safeLocalGet(key) { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; } }
function safeLocalSet(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }
function setUserPref(key, val) { safeLocalSet(key, val); }
function getUserPref(key) { return safeLocalGet(key); }

const money = (n) => (Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ------- sorting helpers -------
function getInitSectionSort(key, fallback = { field: 'date', dir: 'DESC' }) {
  const s = safeLocalGet(key);
  if (s && typeof s.field === 'string' && (s.dir === 'ASC' || s.dir === 'DESC')) return s;
  return fallback;
}
function normalizeDir(d) { return d === 'ASC' ? 'ASC' : 'DESC'; }
function flipDir(d) { return d === 'ASC' ? 'DESC' : 'ASC'; }

// generic safe value extraction for sort
function fieldValue(row, field, keyField) {
  switch (field) {
    case 'id': return Number(row[keyField] || 0);
    case 'client': return String(row.clientname || '').toLowerCase();
    case 'warehouse': return String(row.warehousename || '').toLowerCase();
    case 'date': {
      // Accept raw ISO/SQL string or Date-like string
      const t = row.date ? Date.parse(row.date) : NaN;
      return Number.isNaN(t) ? String(row.date || '') : t;
    }
    case 'charges': return Number(row.chargetotal || 0);
    case 'status': return String(row.receiptstatusname || row.orderstatusname || '').toLowerCase();
    case 'nonbillable': return Number(row.nonbillable || 0);
    case 'notes': return String(row.notes || '').toLowerCase();
    default: return '';
  }
}

function sortRows(rows, sort, keyField) {
  const { field, dir } = sort || { field: 'date', dir: 'DESC' };
  const mult = dir === 'ASC' ? 1 : -1;
  const data = Array.isArray(rows) ? [...rows] : [];
  data.sort((a, b) => {
    const A = fieldValue(a, field, keyField);
    const B = fieldValue(b, field, keyField);
    // numeric/date prefer numeric compare if both are numbers
    const an = typeof A === 'number';
    const bn = typeof B === 'number';
    if (an && bn) return (A === B ? 0 : (A > B ? 1 : -1)) * mult;
    // fallback string compare
    const as = String(A);
    const bs = String(B);
    return (as === bs ? 0 : (as > bs ? 1 : -1)) * mult;
  });
  return data;
}

// sort icon
function SortIcon({ active, dir }) {
  if (!active) return <FaSort style={{ opacity: 0.35, marginLeft: 6 }} />;
  return dir === 'ASC'
    ? <FaSortUp style={{ marginLeft: 6, color: 'var(--primary-color)' }} />
    : <FaSortDown style={{ marginLeft: 6, color: 'var(--primary-color)' }} />;
}

// Format helper: show only YYYY-MM-DD for any date-like value
function dateOnly(v) {
  if (!v) return '';
  const s = String(v);
  // If already looks like YYYY-MM-DD..., trim to 10
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  try { return d.toISOString().slice(0, 10); } catch { return s; }
}

export default function PeriodicInvoicing() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ offset: 0, limit: DEFAULT_LIMIT, total: 0 });
  const [limitInput, setLimitInput] = useState(String(DEFAULT_LIMIT));

  // filters (similar to Invoices)
  const [q, setQ] = useState('');

  // client detail state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailClient, setDetailClient] = useState(null);
  const [detail, setDetail] = useState({ receipts: [], orders: [], eorders: [] });

  const [selR, setSelR] = useState(new Set());
  const [selO, setSelO] = useState(new Set());
  const [selEO, setSelEO] = useState(new Set());

  // inner split sizes for Receipts | Orders | E-Orders
  const [detailSplitSizes, setDetailSplitSizes] = useState(() => {
    const saved = safeLocalGet(DETAIL_SPLIT_KEY);
    if (Array.isArray(saved) && saved.length === 3 && saved.every(x => typeof x === 'number')) return saved;
    return [34, 33, 33]; // default %
  });

  // modal (reserved for future monthly storage flow)
  const [piOpen, setPiOpen] = useState(false);
  const [piClient, setPiClient] = useState(null);
  const [piSelectedIds, setPiSelectedIds] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [piForm, setPiForm] = useState({
    warehouseId: 'all',
    perWarehouse: false,
    month: '',
    invoiceDate: new Date().toISOString().slice(0,10),
    includeAwaitingPickup: false,
  });

  // ----- Create Periodic Invoice modal state -----
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createDate, setCreateDate] = useState(new Date().toISOString().slice(0,10));
  const [createNotes, setCreateNotes] = useState('');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [appendDocNotesToDesc, setAppendDocNotesToDesc] = useState(false);
  const [groupChargesTogether, setGroupChargesTogether] = useState(false);

  // persisted sort for client list (existing)
  const initialSort = useMemo(() => {
    try {
      const saved = safeLocalGet(SORT_KEY) || getUserPref(SORT_KEY);
      return saved || { field: 'name', dir: 'ASC' };
    } catch { return { field: 'name', dir: 'ASC' }; }
  }, []);
  const [sort, setSort] = useState(initialSort);

  // NEW: per-section sort state (persisted)
  const [recSort, setRecSort] = useState(() => getInitSectionSort(RSORT_KEY, { field: 'date', dir: 'DESC' }));
  const [ordSort, setOrdSort] = useState(() => getInitSectionSort(OSORT_KEY, { field: 'date', dir: 'DESC' }));
  const [eorSort, setEorSort] = useState(() => getInitSectionSort(ESORT_KEY, { field: 'date', dir: 'DESC' }));

  useEffect(() => { safeLocalSet(SORT_KEY, sort); setUserPref(SORT_KEY, sort); }, [sort]);
  useEffect(() => { safeLocalSet(RSORT_KEY, recSort); }, [recSort]);
  useEffect(() => { safeLocalSet(OSORT_KEY, ordSort); }, [ordSort]);
  useEffect(() => { safeLocalSet(ESORT_KEY, eorSort); }, [eorSort]);

  const selAllRef = useRef(null);
  useEffect(() => { if (selAllRef.current) selAllRef.current.indeterminate = false; }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(API + '/api/warehouses');
        const j = await res.json();
        setWarehouses(Array.isArray(j) ? j : []);
      } catch { setWarehouses([]); }
    })();
  }, []);

  useEffect(() => {
    const subtitle = '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: detailOpen ? 'Periodic Invoicing — ' + (detailClient?.name || '') : 'Periodic Invoicing', subtitle }
    }));
    return () => { window.dispatchEvent(new CustomEvent('topbar:clearTitle')); };
  }, [meta?.total, detailOpen, detailClient?.name]);

  useEffect(() => { if (!detailOpen) fetchClients(0, meta.limit); }, [q, sort, detailOpen]);
  useEffect(() => { setLimitInput(String(meta.limit || DEFAULT_LIMIT)); }, [meta.limit]);

  async function fetchClients(offset = 0, limit = DEFAULT_LIMIT) {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('offset', String(offset));
      qs.set('limit', String(limit));
      qs.set('activeOnly', '1');
      if (q) qs.set('q', q);
      const sf = (sort && sort.field) ? sort.field : 'name';
      const so = (sort && sort.dir) ? sort.dir : 'ASC';
      qs.set('sortField', sf);
      qs.set('sortOrder', so);
      const res = await fetch(API + '/api/clients?' + qs.toString());
      const j = await res.json();
      const list = Array.isArray(j?.data) ? j.data
        : (Array.isArray(j?.rows) ? j.rows
        : (Array.isArray(j) ? j : []));
      const total = (j?.meta && typeof j.meta.total === 'number') ? j.meta.total
        : (typeof j?.total === 'number' ? j.total : list.length);
      setRows(list);
      setMeta({ offset, limit, total });
    } catch { setRows([]); setMeta({ offset, limit, total: 0 }); }
    finally { setLoading(false); }
  }

  function handleRun() { fetchClients(0, meta.limit); }
  function handleClear() { setQ(''); fetchClients(0, meta.limit); }

  const page = useMemo(() => Math.floor((meta.offset || 0) / (meta.limit || DEFAULT_LIMIT)) + 1, [meta.offset, meta.limit]);
  const lastPage = useMemo(() => Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || DEFAULT_LIMIT))), [meta.total, meta.limit]);
  function gotoPage(p) {
    const limit = meta.limit || DEFAULT_LIMIT;
    const nextOffset = Math.max(0, (p - 1) * limit);
    fetchClients(nextOffset, limit);
  }

  function toggleSort(field) {
    setSort(prev => (prev.field === field ? { field, dir: flipDir(prev.dir) } : { field, dir: 'ASC' }));
  }

  // Toggle selection for a single client row in the top-level list
  function toggleRow(id) {
    setRows(prev => prev.map(r => (
      Number(r.id) === Number(id) ? { ...r, __sel: !r.__sel } : r
    )));
  }

  async function openClientDetail(client) {
    setDetailClient(client);
    setDetailOpen(true);
    // Clear stale data immediately so header counts and panes reset while loading
    setDetail({ receipts: [], orders: [], eorders: [] });
    setDetailLoading(true);
    setSelR(new Set()); setSelO(new Set()); setSelEO(new Set());
    try {
      const res = await fetch(API + '/api/invoices/unbilled?clientid=' + encodeURIComponent(client.id));
      const j = await res.json();
      setDetail({
        receipts: Array.isArray(j?.receipts) ? j.receipts : [],
        orders: Array.isArray(j?.orders) ? j.orders : [],
        eorders: Array.isArray(j?.eorders) ? j.eorders : [],
      });
    } catch { setDetail({ receipts: [], orders: [], eorders: [] }); }
    finally { setDetailLoading(false); }
  }

  // Auto-expand panes that have content but are collapsed
  useEffect(() => {
    if (!detailOpen) return;
    let sizes = Array.isArray(detailSplitSizes) ? [...detailSplitSizes] : [34,33,33];
    const min = 8;
    let changed = false;
    const totals = {
      receipts: (detail?.receipts?.length || 0),
      orders:   (detail?.orders?.length   || 0),
      eorders:  (detail?.eorders?.length  || 0),
    };
    const sum = sizes.reduce((a,b)=>a+(+b||0),0);
    if (!Number.isFinite(sum) || sum < 10) { sizes = [34,33,33]; changed = true; }
    if (totals.orders   > 0 && (sizes[1] == null || sizes[1] < min)) { changed = true; sizes[1] = 33; }
    if (totals.receipts > 0 && (sizes[0] == null || sizes[0] < min)) { changed = true; sizes[0] = 34; }
    if (totals.eorders  > 0 && (sizes[2] == null || sizes[2] < min)) { changed = true; sizes[2] = 33; }
    if (changed) {
      const s = sizes.reduce((a,b)=>a+(+b||0),0) || 100;
      const norm = sizes.map(v => (v / s) * 100);
      setDetailSplitSizes(norm);
      try { localStorage.setItem(DETAIL_SPLIT_KEY, JSON.stringify(norm)); } catch {}
    }
  }, [detailOpen, detail, detailSplitSizes]);

  function backToList() {
    setDetailOpen(false);
    setDetailClient(null);
    setDetail({ receipts: [], orders: [], eorders: [] });
  }

  function toggleSet(setter, current, id) {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  }
  function toggleAllSet(setter, list, key, enabledFilter) {
    return (checked) => {
      if (checked) {
        const ids = list.filter(enabledFilter || (()=>true)).map(r => r[key]);
        setter(new Set(ids));
      } else setter(new Set());
    };
  }

  function DetailHeader() {
    // Reflect the same filters used by the tables so counts match visible rows
    const orderExcluded = (r) => {
      const s = String(r.our_order_status || r.orderstatusname || '').trim().toLowerCase();
      return s === 'skipped' || s === 'error';
    };
    const totalR = detail.receipts.filter(r => Number(r.nonbillable || 0) !== 1).length;
    const totalO = detail.orders.filter(r => Number(r.nonbillable || 0) !== 1 && !orderExcluded(r)).length;
    const totalEO = detail.eorders.filter(eo => String(eo.orderstatusname || '').trim().toLowerCase() !== 'skipped').length;
    return (
      <div className="pi-filters" style={{ alignItems:'center', gap:12 }}>
        <button className="btn" onClick={backToList} title="Back"><FaArrowLeft /> Back</button>
        <div style={{ fontWeight:700 }}>{detailClient?.name}</div>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          <button className="btn" onClick={()=>openClientDetail(detailClient)} title="Refresh">
            <FaSync style={{ verticalAlign:'-2px' }} /> Refresh
          </button>
          <button
            className="btn primary"
            onClick={() => setCreateModalOpen(true)}
            title="Create Master / Periodic Invoice from selections"
            disabled={selR.size + selO.size + selEO.size === 0}
          >
            Create Invoice from Selections ({selR.size + selO.size + selEO.size})
          </button>
          <button
            className="btn"
            onClick={() => {
              const reset = [34,33,33];
              setDetailSplitSizes(reset);
              try { localStorage.setItem(DETAIL_SPLIT_KEY, JSON.stringify(reset)); } catch {}
            }}
            title="Reset pane sizes"
          >
            Reset Layout
          </button>
        </div>
        <div className="pi-subtitle" style={{ width:'100%' }}>
          <small>Unbilled receipts, Orders and E-Orders</small> — Receipts: {totalR} | Orders: {totalO} | E-Orders: {totalEO}
        </div>
      </div>
    );
  }

  // click handlers for section sorts
  const onSortReceipts = (field) => setRecSort(prev => prev.field === field ? { field, dir: flipDir(prev.dir) } : { field, dir: 'ASC' });
  const onSortOrders   = (field) => setOrdSort(prev => prev.field === field ? { field, dir: flipDir(prev.dir) } : { field, dir: 'ASC' });
  const onSortEorders  = (field) => setEorSort(prev => prev.field === field ? { field, dir: flipDir(prev.dir) } : { field, dir: 'ASC' });

  // sorted rows per section
  const sortedReceipts = useMemo(() =>
    sortRows(
      detail.receipts.filter(r => Number(r.nonbillable || 0) !== 1),
      recSort,
      'receiptid'
    ), [detail.receipts, recSort]);

  const sortedOrders = useMemo(() => {
    const orderExcluded = (r) => {
      const s = String(r.our_order_status || r.orderstatusname || '').trim().toLowerCase();
      return s === 'skipped' || s === 'error';
    };
    return sortRows(
      detail.orders.filter(r => Number(r.nonbillable || 0) !== 1 && !orderExcluded(r)),
      ordSort,
      'orderid'
    );
  }, [detail.orders, ordSort])
  
    const sortedEorders = useMemo(() =>
   sortRows(
     detail.eorders.filter(
       eo => String(eo.orderstatusname || '').trim().toLowerCase() !== 'skipped'
     ),
     eorSort,
     'orderid'
   ),
   [detail.eorders, eorSort]
 );

  // enabled if row has any positive charges
  const receiptSelectable = (r) => Number(r.chargetotal || 0) > 0 && Number(r.nonbillable || 0) !== 1;
  const orderSelectable   = (r) => Number(r.chargetotal || 0) > 0 && Number(r.nonbillable || 0) !== 1;
  const eorderSelectable  = (r) => Number(r.chargetotal || 0) > 0;

  // TableBlock: rows already sorted before being passed in
  function TableBlock({
    title, color, rows, keyField,
    enabledFilter, selSet, onToggle, onToggleAll,
    showNonbillable = false,
    sortField, sortDir, onSort
  }) {
    const total = rows.reduce((a, r) => a + Number(r.chargetotal || 0), 0);
    const totalCols = 8 + (showNonbillable ? 1 : 0); // base + optional

    const h = (field, label, style) => (
      <th
        onClick={() => onSort(field)}
        className={sortField === field ? 'active-sort' : ''}
        style={{ cursor:'pointer', userSelect:'none', ...(style||{}) }}
        title={`Sort by ${label}`}
      >
        {label}
        <SortIcon active={sortField === field} dir={sortDir} />
      </th>
    );

    return (
      <div className="pi-table-wrapper" style={{ marginTop: 0 }}>
        <div className="pi-table-title">{title}</div>
        <table className="pi-table">
          <thead>
            <tr>
              <th style={{ width: 34, textAlign:'center' }}>
                <input
                  type="checkbox"
                  checked={rows.filter(enabledFilter).every(r => selSet.has(r[keyField])) && rows.filter(enabledFilter).length > 0}
                  onChange={(e)=>onToggleAll(e.target.checked)}
                />
              </th>
              {h('id', /receipt/i.test(title) ? 'WR#' : 'Order #', { width: 90 })}
              {h('client', 'Client')}
              {h('warehouse', 'Warehouse')}
              {h('date', 'Date')}
              {h('charges', 'Current Charges')}
              {h('status', 'Status')}
              {showNonbillable && h('nonbillable', 'Non-billable')}
              {h('notes', 'Notes')}
            </tr>
          </thead>
          <tbody>
            {detailLoading ? (
              <tr><td colSpan={totalCols} className="pi-center">Loading…</td></tr>
            ) : rows.length ? rows.map((r) => {
                const id = r[keyField];
                const canSelect = enabledFilter(r);
                return (
                  <tr key={id} className={canSelect ? '' : 'pi-row-disabled'}>
                    <td className="pi-center">
                      <input
                        type="checkbox"
                        disabled={!canSelect}
                        checked={selSet.has(id)}
                        onChange={()=>onToggle(id)}
                      />
                    </td>
                    <td className="pi-nowrap">{id}</td>
                    <td className="pi-nowrap">{r.clientname}</td>
                    <td className="pi-nowrap">{r.warehousename}</td>
                    <td className="pi-nowrap">{dateOnly(r.date)}</td>
                    <td className="pi-nowrap" style={{ color: color }}>{'$' + money(r.chargetotal)}</td>
                    <td className="pi-nowrap">{r.receiptstatusname || r.orderstatusname || ''}</td>
                    {showNonbillable && <td className="pi-nowrap">{Number(r.nonbillable || 0) === 1 ? 'Yes' : 'No'}</td>}
                    <td className="pi-nowrap">{r.notes || ''}</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan={totalCols} className="pi-center pi-muted">No rows</td></tr>
              )}
          </tbody>
          {!detailLoading && (
            <tfoot>
              <tr>
                <td></td>
                <td colSpan={4} className="pi-right pi-strong">Total</td>
                <td className="pi-nowrap pi-strong" style={{ color: color }}>{'$' + money(total)}</td>
                <td></td>
                {showNonbillable && <td></td>}
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  }

  // Resolve warehouse id from a display name when a line doesn't include one
  function resolveWarehouseIdByName(name) {
    if (!name) return null;
    const n = String(name).trim().toLowerCase();
    const hit = (warehouses || []).find(w => String(w.name || '').trim().toLowerCase() === n);
    return hit ? Number(hit.id) : null;
  }

  // Compose and post periodic invoice from selections
  async function handleCreatePeriodicInvoice() {
    if (!detailClient?.id) return;
    setCreatingInvoice(true);
    try {
      const items = [];

      // Receipts -> combined (includes storage)
      for (const rid of Array.from(selR)) {
        const r = await fetch(`${API}/api/receipts/${rid}/charges/combined`);
        if (!r.ok) continue;
        const jr = await r.json();
        const lines = Array.isArray(jr?.lines) ? jr.lines : (Array.isArray(jr?.data) ? jr.data : []);
        const rRow = (detail?.receipts || []).find(rc => Number(rc.receiptid) === Number(rid));
        const rDesc = `Warehouse Receipt #${rid}${(appendDocNotesToDesc && rRow?.notes) ? ' - ' + String(rRow.notes).trim() : ''}`;
        for (const li of (lines || [])) {
          const qty = Number(li.qty ?? li.count ?? 0);
          const amt = Number(li.amt ?? 0);
          if (qty <= 0 || !isFinite(amt)) continue;
          items.push({
            qty,
            amt,
            chargeitemid: Number(li.chargeid || li.chargeitemid || 0),
            chargename: li.custom || li.name || li.chargename || '',
            description: rDesc,
            chargetype: 1,
            reference: Number(rid),
            warehouseid: (li.warehouseid != null ? Number(li.warehouseid) : resolveWarehouseIdByName(rRow?.warehousename)) ?? null,
          });
        }
      }

      // Orders -> normalized via helper
      for (const oid of Array.from(selO)) {
        const r = await fetch(`${API}/api/invoices/orders/${oid}/charges`);
        if (!r.ok) continue;
        const jo = await r.json();
        const oRow = (detail?.orders || []).find(orw => Number(orw.orderid) === Number(oid));
        const oDesc = `Order #${oRow?.orderid || oid}${(appendDocNotesToDesc && oRow?.notes) ? ' - ' + String(oRow.notes).trim() : ''}`;
        for (const li of (jo?.lines || [])) {
          const qty = Number(li.qty || 0);
          const amt = Number(li.amt || 0);
          if (qty <= 0 || !isFinite(amt)) continue;
          items.push({
            qty, amt,
            chargeitemid: Number(li.chargeitemid || 0),
            chargename: li.chargename || '',
            description: oDesc,
            chargetype: 2,
            reference: Number(oid),
            warehouseid: (li.warehouseid != null ? Number(li.warehouseid) : resolveWarehouseIdByName(oRow?.warehousename)) ?? null,
          });
        }
      }

      // E-Orders -> normalized via helper (resolve numeric id from displayed ordernumber)
      for (const shown of Array.from(selEO)) {
        // 'shown' is the display key in the table (ordernumber). Find row to get numeric id.
        const row = (detail?.eorders || []).find(e => e.orderid === shown || e.id === shown);
        const eid = row?.id != null ? Number(row.id) : Number(shown);
        if (!Number.isFinite(eid) || eid <= 0) continue;
        const shownOrderNo = row?.orderid || shown;
        const eDesc = `E-Order #${shownOrderNo}${(appendDocNotesToDesc && row?.notes) ? ' - ' + String(row.notes).trim() : ''}`;

        const r = await fetch(`${API}/api/invoices/eorders/${eid}/charges`);
        if (!r.ok) continue;
        const je = await r.json();
        for (const li of (je?.lines || [])) {
          const qty = Number(li.qty || 0);
          const amt = Number(li.amt || 0);
          if (qty <= 0 || !isFinite(amt)) continue;
          items.push({
            qty, amt,
            chargeitemid: Number(li.chargeitemid || 0),
            chargename: li.chargename || '',
            description: eDesc,
            chargetype: 6,
            reference: Number(eid),
            warehouseid: (li.warehouseid != null ? Number(li.warehouseid) : resolveWarehouseIdByName(row?.warehousename)) ?? null,
          });
        }
      }

      if (!items.length) {
        alert('No billable lines were found for the selected documents.');
        setCreatingInvoice(false);
        return;
      }

      // Optionally group identical charges into single lines
      let finalItems = items;
      if (groupChargesTogether) {
        const map = new Map();
        for (const it of items) {
          const key = [
            it.chargeitemid ?? '',
            isFinite(it.amt) ? Number(it.amt) : '',
            it.warehouseid ?? '',
            it.chargename ?? ''
          ].join('|');
          const prev = map.get(key);
          if (prev) {
            prev.qty += Number(it.qty || 0);
          } else {
            // Use a concise description when grouping
            map.set(key, {
              ...it,
              description: it.chargename || 'Grouped charges',
            });
          }
        }
        finalItems = Array.from(map.values());
      }

      const receiptIds = Array.from(selR).map(id => Number(id)).filter(n => Number.isFinite(n) && n > 0);
      const orderIds   = Array.from(selO).map(id => Number(id)).filter(n => Number.isFinite(n) && n > 0);
      const eorderIds  = Array.from(selEO)
        .map(shown => {
          const row = (detail?.eorders || []).find(e => e.orderid === shown || e.id === shown);
          return row?.id != null ? Number(row.id) : Number(shown);
        })
        .filter(n => Number.isFinite(n) && n > 0);

      const payload = {
        clientid: Number(detailClient.id),
        date: createDate || new Date().toISOString().slice(0,10),
        notes: createNotes || 'Periodic Invoice',
        items: finalItems,
        userFullName: (JSON.parse(localStorage.getItem('user') || '{}')?.fullname) || 'system',
        type: 4,
        // Hint for backend/logging if needed
        groupChargesTogether: !!groupChargesTogether,
        receiptIds,
        orderIds,
        eorderIds,
      };

      const res = await fetch(`${API}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const j = await res.json();
      if (!res.ok || !j?.id) {
        alert(j?.error || 'Failed to create invoice');
        setCreatingInvoice(false);
        return;
      }

      setCreatingInvoice(false);
      setCreateModalOpen(false);
      window.open(`${API}/api/invoices/${j.id}/pdf?title=${encodeURIComponent('Periodic Invoice')}`, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setCreatingInvoice(false);
      alert(e.message || 'Failed to create invoice');
    }
  }

  // ========= RENDER =========
  if (detailOpen) {
    return (
      <div className="pi-wrapper">
        <DetailHeader />

        {/* Three resizable panes: Receipts | Orders | E-Orders (remain separate) */}
        <div className="pi-detail-body">
          <Split
            direction="vertical"
            sizes={detailSplitSizes}
            minSize={[160, 140, 140]}
            gutterSize={10}
            gutterAlign="center"
            cursor="row-resize"
            className="pi-vert-split"
            style={{ height: 'calc(100vh - 180px)' }}
            onDragEnd={(sizes) => {
              setDetailSplitSizes(sizes);
              safeLocalSet(DETAIL_SPLIT_KEY, sizes);
            }}
          >
            {/* Receipts & Workorders */}
            <div className="pi-pane">
              <TableBlock
                title="Unbilled receipts"
                color="#2e7d32"
                rows={sortedReceipts}
                keyField="receiptid"
                enabledFilter={receiptSelectable}
                selSet={selR}
                onToggle={(id)=>toggleSet(setSelR, selR, id)}
                onToggleAll={toggleAllSet(setSelR, sortedReceipts, 'receiptid', receiptSelectable)}
                showNonbillable={true}
                sortField={recSort.field}
                sortDir={recSort.dir}
                onSort={onSortReceipts}
              />
            </div>

            {/* Orders */}
            <div className="pi-pane">
              <TableBlock
                title="Orders"
                color="#c62828"
                rows={sortedOrders}
                keyField="orderid"
                enabledFilter={orderSelectable}
                selSet={selO}
                onToggle={(id)=>toggleSet(setSelO, selO, id)}
                onToggleAll={toggleAllSet(setSelO, sortedOrders, 'orderid', orderSelectable)}
                sortField={ordSort.field}
                sortDir={ordSort.dir}
                onSort={onSortOrders}
              />
            </div>

            {/* E-Orders */}
            <div className="pi-pane">
              <TableBlock
                title="E-Orders"
                color="#5e35b1"
                rows={sortedEorders}
                keyField="orderid"
                enabledFilter={eorderSelectable}
                selSet={selEO}
                onToggle={(id)=>toggleSet(setSelEO, selEO, id)}
                onToggleAll={toggleAllSet(setSelEO, sortedEorders, 'orderid', eorderSelectable)}
                sortField={eorSort.field}
                sortDir={eorSort.dir}
                onSort={onSortEorders}
              />
            </div>
          </Split>
        </div>
        {createModalOpen && (
          <div className="modal-overlay" onMouseDown={(e)=>{ if (e.target===e.currentTarget && !creatingInvoice) setCreateModalOpen(false); }}>
            <div className="modal" role="dialog" aria-modal="true" aria-labelledby="piCreateTitle" onMouseDown={e=>e.stopPropagation()}>
              <h2 id="piCreateTitle">Create Periodic Invoice</h2>
              <div className="payment-form">
                <div className="form-grid" style={{ gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap: '0.6rem 0.8rem' }}>
                  <label>Date
                    <input type="date" value={createDate} onChange={e=>setCreateDate(e.target.value)} />
                  </label>
                  <label className="span-2">Notes
                    <input type="text" value={createNotes} onChange={e=>setCreateNotes(e.target.value)} placeholder="Optional notes (e.g., Periodic Invoice Sep-2024)" />
                  </label>
                </div>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    id="append-notes"
                    type="checkbox"
                    checked={appendDocNotesToDesc}
                    onChange={e => setAppendDocNotesToDesc(e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  <label htmlFor="append-notes">Append document notes to the Description column</label>
                </div>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    id="group-charges"
                    type="checkbox"
                    checked={groupChargesTogether}
                    onChange={e => setGroupChargesTogether(e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  <label htmlFor="group-charges">Group charges together</label>
                </div>
              </div>
              <div className="modal-actions" style={{ justifyContent:'flex-end', marginTop: 12 }}>
                <button className="btn" onClick={()=>!creatingInvoice && setCreateModalOpen(false)} disabled={creatingInvoice}>Cancel</button>
                <button className="btn primary" onClick={handleCreatePeriodicInvoice} disabled={creatingInvoice || (!selR.size && !selO.size && !selEO.size)}>
                  {creatingInvoice ? 'Creating…' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="pi-wrapper">
      <div className="pi-filters">
        <div className="pi-preset">
          <div className="pi-search">
            <FaSearch />
            <input
              type="text"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Search clients…"
            />
            {q && (
              <button
                className="clear"
                aria-label="Clear search"
                title="Clear"
                onClick={() => { setQ(''); fetchClients(0, meta.limit); }}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>
        <button className="btn" onClick={handleRun} title="Run">Run</button>
        <button className="btn" onClick={handleClear} title="Reset">Reset</button>
        <button
          className="btn primary"
          disabled={!rows.some(r => r.__sel)}
          onClick={()=>{
            const first = rows.find(r => r.__sel);
            if (first) openClientDetail(first);
          }}
          title="Create for selected"
        >
          Create for Selected ({rows.filter(r => r.__sel).length || 0})
        </button>
      </div>

      <div className="pi-table-wrapper">
        <table className="pi-table no-title">
          <thead>
            <tr>
              <th style={{ width: 34, textAlign:'center' }}>
                <input
                  type="checkbox"
                  ref={selAllRef}
                  checked={rows.length > 0 && rows.every(r => r.__sel)}
                  onChange={(e)=>{
                    const v = e.target.checked;
                    setRows(prev => prev.map(r => ({ ...r, __sel: v })));
                  }}
                />
              </th>
              <th className="pi-actions">Actions</th>
              <th onClick={()=>toggleSort('id')} className={sort.field==='id' ? 'pi-sort-active' : ''}>
                ID <SortIcon active={sort.field==='id'} dir={sort.dir}/>
              </th>
              <th onClick={()=>toggleSort('name')} className={sort.field==='name' ? 'pi-sort-active' : ''}>
                Name <SortIcon active={sort.field==='name'} dir={sort.dir}/>
              </th>
              <th onClick={()=>toggleSort('contact')} className={sort.field==='contact' ? 'pi-sort-active' : ''}>
                Contact <SortIcon active={sort.field==='contact'} dir={sort.dir}/>
              </th>
              <th onClick={()=>toggleSort('email')} className={sort.field==='email' ? 'pi-sort-active' : ''}>
                Email <SortIcon active={sort.field==='email'} dir={sort.dir}/>
              </th>
              <th onClick={()=>toggleSort('paymentterms')} className={sort.field==='paymentterms' ? 'pi-sort-active' : ''}>
                Terms <SortIcon active={sort.field==='paymentterms'} dir={sort.dir}/>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td className="pi-center">
                  <input
                    type="checkbox"
                    checked={!!c.__sel}
                    onChange={()=>toggleRow(c.id)}
                  />
                </td>
                <td className="pi-actions">
                  <button className="mini-btn" title="Create periodic invoice" onClick={()=>openClientDetail(c)}>
                    <FaCalendarAlt size={16} />
                  </button>
                </td>
                <td className="pi-nowrap">{c.id}</td>
                <td className="pi-nowrap">{c.name}</td>
                <td className="pi-nowrap">{c.contact}</td>
                <td className="pi-nowrap">{c.email}</td>
                <td className="pi-nowrap">{c.paymentterms}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="pi-center pi-muted">No clients found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination" style={{ display:'flex', alignItems:'center', gap:12, marginTop:12 }}>
        <button className="btn" disabled={page <= 1} onClick={() => gotoPage(1)}>&laquo;</button>
        <button className="btn" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>Prev</button>
        <span>Page {page} of {lastPage}</span>
        <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(page + 1)}>Next</button>
        <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(lastPage)}>&raquo;</button>
        <label style={{ marginLeft: 12, fontSize: 13 }}>
          Rows per page:&nbsp;
          <input
            type="number"
            min={1}
            max={500}
            value={limitInput}
            style={{ width: 60, fontSize: 13, padding: '2px 4px' }}
            onChange={e => setLimitInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { const v = Math.max(1, Math.min(500, Number(limitInput) || DEFAULT_LIMIT)); fetchClients(0, v); } }}
            onBlur={() => { const v = Math.max(1, Math.min(500, Number(limitInput) || DEFAULT_LIMIT)); fetchClients(0, v); }}
          />
        </label>
      </div>

      {piOpen && (
        <div className="modal-overlay" onMouseDown={(e)=>{ if (e.target===e.currentTarget) setPiOpen(false); }}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="piTitle">
            <h2 id="piTitle" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FaCalendarAlt /> Create Periodic Invoice{piSelectedIds?.length > 1 ? ' — ' + piSelectedIds.length + ' clients' : (piClient?.name ? ' — ' + piClient.name : '')}
            </h2>
            <form className="payment-form" onSubmit={(e)=>{e.preventDefault(); setPiOpen(false);}}>
              <div className="form-grid" style={{ gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap: '0.6rem 0.8rem' }}>
                <label>Warehouse(s) selection
                  <select
                    value={piForm.warehouseId}
                    onChange={(e)=>setPiForm(f=>({ ...f, warehouseId: e.target.value }))}
                  >
                    <option value="all">All</option>
                    {warehouses.map(w => (
                      <option key={w.id} value={String(w.id)}>{w.name || `Warehouse #${w.id}`}</option>
                    ))}
                  </select>
                </label>

                <label>Which month is this invoice for?
                  <input
                    type="date"
                    value={piForm.month}
                    onChange={(e)=>setPiForm(f=>({ ...f, month: e.target.value }))}
                    placeholder="mm/dd/yyyy"
                  />
                </label>

                <label>Date For Invoice (Date invoice created)
                  <input
                    type="date"
                    value={piForm.invoiceDate}
                    onChange={(e)=>setPiForm(f=>({ ...f, invoiceDate: e.target.value }))}
                  />
                </label>

                <div style={{ display:'flex', gap:'2rem', marginTop:'0.5rem', gridColumn:'1 / -1' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input
                      type="checkbox"
                      checked={piForm.perWarehouse}
                      onChange={(e)=>setPiForm(f=>({ ...f, perWarehouse: e.target.checked }))}
                    />
                    <span>Separate Invoice For Each Warehouse Location</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input
                      type="checkbox"
                      checked={piForm.includeAwaitingPickup}
                      onChange={(e)=>setPiForm(f=>({ ...f, includeAwaitingPickup: e.target.checked }))}
                    />
                    <span>Include pallets from orders awaiting pickup?</span>
                  </label>
                </div>
              </div>

              <div className="modal-actions" style={{ justifyContent:'flex-end', marginTop: 12 }}>
                <button type="button" className="btn" onClick={()=>setPiOpen(false)}>Cancel</button>
                <button type="submit" className="btn primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
